import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DocumentsTable from "./DocumentsTable";
import FilesGrid from "./FilesGrid";
import PdfSourceModal from "./PdfSourceModal";
import { useAppSelector } from "@store/hooks";
import { getWorkspaces } from "@services/api";
import { OwnedWorkspace } from "./AddToWorkspaceMenu";

type ViewMode = "list" | "grid";
const VIEW_MODE_KEY = "files.viewMode";

// /files is the user-scoped "library" view of documents. Documents are
// owned by the uploader (not the workspace) so this surface deliberately
// lives outside the workspace admin shell. The current workspace context
// is still available globally; rows/cards expose the "in current
// workspace" badge and the unlink-from-current-workspace action.
const FilesScreen = () => {
  const userId = useAppSelector((s) => s.user.userId);
  const [modalVisible, setModalVisible] = useState("");
  // Bumped on every successful upload so the underlying table/grid
  // re-fetches without prop-drilling a callback chain.
  const [refreshKey, setRefreshKey] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === "undefined") return "list";
    const persisted = window.localStorage.getItem(VIEW_MODE_KEY);
    return persisted === "grid" ? "grid" : "list";
  });

  // Workspaces the user owns. Drives the "Add to workspace…" picker
  // in both the list and grid views. Fetched once on mount and
  // re-fetched on refreshKey so a freshly-added workspace appears in
  // the picker without a full reload.
  const [ownedWorkspaces, setOwnedWorkspaces] = useState<OwnedWorkspace[]>(
    []
  );

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    void (async () => {
      const result = await getWorkspaces();
      if (cancelled) return;
      if (!result.success) {
        console.error("FilesScreen: failed to load workspaces", result.error);
        return;
      }
      const list = Array.isArray(result.data) ? result.data : [];
      const owned = list
        .filter((ws) =>
          (ws.workspace_permissions ?? []).some(
            (p) => p.user_id === userId && p.permission_name === "owner"
          )
        )
        .map((ws) => ({
          workspaceId: ws.workspace_id,
          workspaceName: ws.workspace_name,
        }));
      setOwnedWorkspaces(owned);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, refreshKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(VIEW_MODE_KEY, viewMode);
  }, [viewMode]);

  const handleViewChange = (_: unknown, next: ViewMode | null) => {
    if (next) setViewMode(next);
  };

  const requestRefresh = useCallback(
    () => setRefreshKey((k) => k + 1),
    []
  );
  const ownedListProp = useMemo(() => ownedWorkspaces, [ownedWorkspaces]);

  return (
    <Box sx={{ width: "100%", p: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="h5">Files</Typography>
          <Typography variant="body2" color="text.secondary">
            Documents you've uploaded. Each file lives independent of any
            workspace; add a file to a workspace from here or via the
            Resources tab.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={handleViewChange}
            aria-label="files view mode"
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewListOutlinedIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <GridViewOutlinedIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            size="small"
            startIcon={<UploadFileOutlinedIcon />}
            onClick={() => setModalVisible("choosePdf")}
          >
            Upload
          </Button>
        </Box>
      </Box>

      {viewMode === "list" ? (
        <DocumentsTable
          setModalVisible={setModalVisible}
          modalVisible={modalVisible}
          refreshKey={refreshKey}
          hideUploadButton
          ownedWorkspaces={ownedListProp}
          onRequestRefresh={requestRefresh}
        />
      ) : (
        <FilesGrid
          refreshKey={refreshKey}
          ownedWorkspaces={ownedListProp}
          onRequestRefresh={requestRefresh}
        />
      )}

      <PdfSourceModal
        modalVisible={modalVisible}
        setModalVisible={setModalVisible}
        onUploaded={requestRefresh}
      />
    </Box>
  );
};

export default FilesScreen;
